import { motion } from 'framer-motion';
import { FileText, Globe, MessageSquare, Workflow, Palette, Zap, BarChart2, UserCheck } from 'lucide-react';

const features = [
  { icon: Zap,          title: 'Always On, Zero Downtime',              desc: 'Responds in milliseconds, 24/7, 365 days.',   gradA: '#479EC3', gradB: '#6366f1' },
  { icon: FileText,     title: 'Train on PDFs, Docs, FAQs & Websites', desc: 'Understands your business instantly.',         gradA: '#479EC3', gradB: '#22D3EE' },
  { icon: MessageSquare,title: 'Human-like Conversations',              desc: 'Your AI, trained your way.',                  gradA: '#6366f1', gradB: '#479EC3' },
  { icon: Palette,      title: 'Brand Voice Personalization',           desc: 'Sounds exactly like your team.',              gradA: '#10b981', gradB: '#479EC3' },
  { icon: Globe,        title: 'Omnichannel Deployment',                desc: 'Deploy once. Support everywhere.',            gradA: '#22D3EE', gradB: '#6366f1' },
  { icon: Workflow,     title: 'AI Workflow Automation',                desc: 'Automate repetitive tasks end-to-end.',       gradA: '#479EC3', gradB: '#22D3EE' },
  { icon: BarChart2,    title: 'Smart Analytics & Insights',            desc: 'Know what your customers really want.',       gradA: '#22D3EE', gradB: '#10b981', comingSoon: true },
  { icon: UserCheck,    title: 'Human Handoff',                         desc: 'Seamlessly escalate complex issues.',         gradA: '#6366f1', gradB: '#22D3EE', comingSoon: true },
];

export default function FeaturesSection() {
  return (
    <section id="features" style={{ padding: '100px 24px', position: 'relative', overflow: 'hidden' }}>
      {/* Glow */}
      <div style={{ position: 'absolute', top: '30%', right: 0, width: 400, height: 400, background: 'rgba(34,211,238,0.05)', borderRadius: '50%', filter: 'blur(100px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 64 }}>
          <p style={{ fontSize: 13, color: '#479EC3', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Features</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, lineHeight: 1.15, marginBottom: 16, letterSpacing: '-0.02em' }}>
            Everything you need to automate support<br />and capture more leads
          </h2>
          <p style={{ fontSize: 17, color: '#64748b', maxWidth: 520, margin: '0 auto' }}>
            Built for teams that want real results — not just another chatbot.
          </p>
        </motion.div>

        <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                whileHover={{ y: -4 }}
                className="feature-card"
                style={{
                  background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(30,41,59,0.6)', borderRadius: 14, padding: 24,
                  transition: 'border-color 0.3s, box-shadow 0.3s, transform 0.3s',
                  cursor: 'default', position: 'relative', overflow: 'hidden',
                  opacity: f.comingSoon ? 0.7 : 1,
                }}>
                {f.comingSoon && (
                  <span style={{
                    position: 'absolute', top: 14, right: 14,
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                    background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)',
                    color: '#a5b4fc', borderRadius: 6, padding: '3px 8px',
                  }}>Coming Soon</span>
                )}
                <div style={{
                  width: 48, height: 48, borderRadius: 12, marginBottom: 16,
                  background: `linear-gradient(135deg,${f.gradA},${f.gradB})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 0 20px ${f.gradA}44`,
                }}>
                  <Icon size={22} color="#fff" />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 8, lineHeight: 1.3 }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: 0 }}>{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) { .features-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 540px)  { .features-grid { grid-template-columns: 1fr !important; } }
        .feature-card:hover { border-color: rgba(71,158,195,0.4) !important; box-shadow: 0 0 32px rgba(71,158,195,0.15); }
      `}</style>
    </section>
  );
}
